/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

/** A single curated link in an `llms.txt` section. */
export interface LlmsLink {
	/** Human-readable link label. */
	title: string;
	/** Absolute or root-relative URL. Root-relative values are prefixed with the resolved base URL. */
	url: string;
	/** Optional short description shown after the link. */
	notes?: string;
}

/** A titled group of links rendered as a `## Heading` section. */
export interface LlmsSection {
	title: string;
	links: LlmsLink[];
}

/** Input for {@link renderLlmsTxt}. */
export interface LlmsConfig {
	/** Site/product name — rendered as the top-level `# Heading`. */
	title: string;
	/** One-line summary — rendered as the leading `>` blockquote. */
	summary: string;
	/**
	 * Canonical site origin (may include a base path, e.g. `https://www.fab.com/o`).
	 * Defaults to `EPIC_SERVER_URL` + `COSMOS_SITE_BASE_PATH` — the same join used by
	 * the robots/sitemap helpers. Used to resolve root-relative link URLs and the sitemap link.
	 */
	baseUrl?: string;
	/** Curated link groups. */
	sections?: LlmsSection[];
	/** Append a `## Sitemaps` section linking `${baseUrl}/sitemap.xml`. Defaults to `true`. */
	includeSitemap?: boolean;
}

// Lazily resolve the base URL to avoid calling env accessors at module-evaluation time
// (this module is pulled into client bundles via barrel exports, where EPIC_SERVER_URL
// is unavailable). Mirrors the approach in ./robots.ts.
function getDefaultBaseUrl(): string {
	return `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}`;
}

function stripTrailingSlash(value: string): string {
	return value.endsWith("/") ? value.slice(0, -1) : value;
}

/** Resolve a link URL against the base URL when it is root-relative. */
function resolveUrl(url: string, baseUrl: string): string {
	if (url.startsWith("http://") || url.startsWith("https://")) return url;
	return `${stripTrailingSlash(baseUrl)}${url.startsWith("/") ? url : `/${url}`}`;
}

function renderSection(section: LlmsSection, baseUrl: string): string {
	const links = section.links.map((link) => {
		const label = `[${link.title}](${resolveUrl(link.url, baseUrl)})`;
		return link.notes ? `- ${label}: ${link.notes}` : `- ${label}`;
	});
	return [`## ${section.title}`, "", ...links].join("\n");
}

/**
 * Render an `llms.txt` document (Markdown) from a curated config.
 * Format follows the llms.txt convention: an `# H1` title, a `>` summary blockquote,
 * curated `## Section` link lists, and (by default) a `## Sitemaps` link.
 */
export function renderLlmsTxt(config: LlmsConfig): string {
	const baseUrl = config.baseUrl ?? getDefaultBaseUrl();
	const sections = [...(config.sections ?? [])];

	if (config.includeSitemap !== false) {
		sections.push({ title: "Sitemaps", links: [{ title: "Sitemap", url: "/sitemap.xml" }] });
	}

	const blocks = [
		`# ${config.title}`,
		`> ${config.summary}`,
		...sections.map((section) => renderSection(section, baseUrl)),
	];

	// Join blocks with a blank line between each and terminate with a trailing newline.
	return `${blocks.join("\n\n")}\n`;
}

/**
 * Build a Next.js Route Handler (`GET`) that serves an `llms.txt`, mirroring the thin
 * `robots.ts` wiring. Accepts either a static config or a (sync/async) factory so
 * multi-tenant apps can resolve per-request config.
 *
 * Only `GET` is exported by callers, so Next.js returns `405` for other methods.
 */
export function createLlmsTxtRoute(
	config: LlmsConfig | (() => LlmsConfig | Promise<LlmsConfig>),
): () => Promise<Response> {
	return async function GET(): Promise<Response> {
		const resolved = typeof config === "function" ? await config() : config;
		return new Response(renderLlmsTxt(resolved), {
			headers: {
				"content-type": "text/plain; charset=utf-8",
				"cache-control": "public, max-age=3600",
			},
		});
	};
}
